import { expiryMeterPercent, expiryTone, type ExpiryState } from '@chicare/shared';
import { cn } from '@/lib/cn';

/**
 * ExpiryMeter.
 *
 * The single most ChiCare component in the system. Expiry is the thing a
 * Nigerian buyer of medicine is genuinely afraid of, and this is the one place
 * the app answers it without them having to squint at a foil.
 *
 * §11, colour is semantic: green while there is plenty of shelf life, amber as
 * it runs out, red when the batch is below the minimum ChiCare will sell or
 * already expired. Nothing here is decorative and nothing else in the app may
 * use these colours for anything else.
 *
 * The bar is scaled against a 24-month nominal shelf life rather than against
 * each product's own maximum, so a half-full bar means the same thing on
 * paracetamol as on an inhaler. A per-product axis would make a 6-month pack
 * and a 24-month pack look identical, which is the exact confusion this exists
 * to remove.
 */

const fills: Record<ReturnType<typeof expiryTone>, string> = {
  verified: 'bg-verified',
  caution: 'bg-caution',
  danger: 'bg-danger',
  brand: 'bg-blue',
  neutral: 'bg-line',
};

export interface ExpiryMeterProps {
  state: ExpiryState | null | undefined;
  monthsRemaining: number | null | undefined;
  /** e.g. "Mar 2028". Rendered at the right end of the scale. */
  expiryLabel?: string | null;
  /** Hide the "now / N months left / date" scale, for tight rows. */
  compact?: boolean;
  className?: string;
}

export function ExpiryMeter({
  state,
  monthsRemaining,
  expiryLabel,
  compact = false,
  className,
}: ExpiryMeterProps) {
  const tone = expiryTone(state);
  const percent = expiryMeterPercent(monthsRemaining);

  const readout =
    state === 'expired'
      ? 'Expired'
      : state === 'below_minimum'
        ? 'Below our minimum'
        : monthsRemaining
          ? `${monthsRemaining} months left`
          : 'Expiry unknown';

  const toneText =
    tone === 'verified'
      ? 'text-verified'
      : tone === 'caution'
        ? 'text-caution'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-faint';

  return (
    <div className={className}>
      <div
        className="relative h-1.5 overflow-hidden rounded-[3px] bg-line-2"
        role="meter"
        aria-valuenow={monthsRemaining ?? 0}
        aria-valuemin={0}
        aria-valuemax={24}
        aria-label={`Shelf life remaining: ${readout}`}
      >
        <i
          className={cn('block h-full rounded-[3px] transition-[width]', fills[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>

      {!compact && (
        <div className="mt-1 flex justify-between font-mono text-[9.5px] text-faint tabular">
          <span>now</span>
          <span className={toneText}>{readout}</span>
          <span>{expiryLabel ?? '—'}</span>
        </div>
      )}
    </div>
  );
}
